/* eslint-disable prettier/prettier */
import React, { useEffect, useState, useRef } from 'react';
import styles from '../../../../styles/components/pageComponents/program/EditProgramGoals.module.css';
import {
    Goal,
    UpdateGoalPayload,
    CreateGoalPayload,
} from '../../../../services/goal.service';
import {
    
	GoalMutationResult,
} from '../../../../hooks/useGoal';
import { useGoalType } from '../../../../hooks/useGoalType';

interface GoalItem {
    id: string;
    isNew?: boolean;
    type: string;
    goalTypeId?: string;
    description: string;
    targetValue: string;
    unit: string;
}

interface GoalType {
    id: string;
    name: string;
}


interface EditProgramGoalsProps {
    onClose: () => void;
    goals: Goal[];
    goalTypes: GoalType[];
    units: string[];

    createGoal: (
        payload: CreateGoalPayload
    ) => Promise<GoalMutationResult>;

    updateGoal: (
        goalId: string,
        payload: UpdateGoalPayload
    ) => Promise<GoalMutationResult>;

    deleteGoal: (goalId: string) => Promise<boolean>;

    fetchGoals: () => Promise<void>;
    fetchGoalTypes: () => Promise<void>;
    onSuccess: (title: string, subtitle: string) => void;
    onError: (error: any) => void;
    userId: string;
    programId: string;
}

/* Custom Select Dropdown Component to match custom UI */
interface Option {
    label: string;
    value: string;
}

interface CustomSelectProps {
    options: Option[];
    value: string;
    placeholder: string;
    hasError?: boolean;
    onChange: (value: string) => void;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    options,
    value,
    placeholder,
    hasError,
    onChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(opt => opt.value === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={styles.customSelectContainer} ref={dropdownRef}>
            <div
                className={`${styles.selectTrigger} ${
                    hasError ? styles.inputErrorBorder : ''
                }`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span
                    className={
                        !selectedOption
                            ? styles.placeholderText
                            : styles.selectedText
                    }
                >
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>
                    ▼
                </span>
            </div>

            {isOpen && (
                <div className={styles.dropdownMenu}>
                    {options.map(opt => (
                        <div
                            key={opt.value}
                            className={`${styles.dropdownItem} ${
                                opt.value === value ? styles.activeItem : ''
                            }`}
                            onClick={() => {
                                onChange(opt.value);
                                setIsOpen(false);
                            }}
                        >
                            {opt.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const TrashIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="22"
        viewBox="0 0 20 22"
        fill="none"
    >
        <path
            d="M7.75 9.75V15.75M11.75 9.75V15.75M16.75 4.75V18.75C16.75 19.2804 16.5393 19.7891 16.1642 20.1642C15.7891 20.5393 15.2804 20.75 14.75 20.75H4.75C4.21957 20.75 3.71086 20.5393 3.33579 20.1642C2.96071 19.7891 2.75 19.2804 2.75 18.75V4.75M0.75 4.75H18.75M5.75 4.75V2.75C5.75 2.21957 5.96071 1.71086 6.33579 1.33579C6.71086 0.960714 7.21957 0.75 7.75 0.75H11.75C12.2804 0.75 12.7891 0.960714 13.1642 1.33579C13.5393 1.71086 13.75 2.21957 13.75 2.75V4.75"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const CheckIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 27 27"
        fill="none"
    >
        <path
            d="M25.4983 10.7542C26.0692 13.5559 25.6623 16.4685 24.3456 19.0065C23.0289 21.5444 20.8819 23.5543 18.2627 24.7009C15.6435 25.8475 12.7103 26.0615 9.95244 25.3072C7.19453 24.5529 4.77856 22.876 3.10742 20.556C1.43627 18.236 0.610975 15.4133 0.769153 12.5585C0.92733 9.70367 2.05942 6.98937 3.97663 4.86822C5.89385 2.74707 8.48029 1.3473 11.3046 0.902326C14.129 0.457355 17.0205 0.994084 19.4971 2.42301M9.49706 12.0042L13.2471 15.7542L25.747 3.25426"
            stroke="#147B58"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const EditProgramGoals: React.FC<EditProgramGoalsProps> = ({
    onClose,
    goals,
    updateGoal,
    deleteGoal,
    fetchGoals,
    fetchGoalTypes,
    goalTypes,
    units,
    userId,
    programId,
    createGoal,
    onSuccess,
    onError,
}) => {

        const { creating, createGoalType } = useGoalType();

    const [showNewGoalTypeModal, setShowNewGoalTypeModal] =
        useState(false);

    const [newGoalTypeName, setNewGoalTypeName] = useState('');

    const [newGoalTypeError, setNewGoalTypeError] = useState('');

    const [localGoals, setLocalGoals] = useState<GoalItem[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
    setLocalGoals(
        goals.map(goal => ({
            id: goal.id,
            type: goal.goaltype || '',
            goalTypeId: goal.goaltypeid || '',
            description: goal.description || '',
            targetValue: goal.targetvalue?.toString() || '',
            unit: goal.unit || '',
        }))
    );
}, [goals]);

    const handleTargetValueChange = (id: string, value: string) => {
        if (value === '') {
            handleInputChange(id, 'targetValue', '');
            setErrors(prev => ({ ...prev, [id]: '' }));
            return;
        }

        if (/^\d+$/.test(value)) {
            handleInputChange(id, 'targetValue', value);
            setErrors(prev => ({ ...prev, [id]: '' }));
            return;
        }

        setErrors(prev => ({
            ...prev,
            [id]: 'Only numeric values are allowed',
        }));
    };


    const handleAddGoal = () => {
        setLocalGoals(prev => [
            ...prev,
            {
                id: crypto.randomUUID(),
                isNew: true,
                type: '',
                goalTypeId: '',
                description: '',
                targetValue: '',
                unit: '',
            },
        ]);
    };

    const handleDeleteGoal = async (id: string) => {
        const existingGoal = goals.find(goal => goal.id === id);

        if (!existingGoal) {
            setLocalGoals(prev => prev.filter(goal => goal.id !== id));
            return;
        }

        const success = await deleteGoal(id);
        if (success) {
            onSuccess(
                'Goal Deleted Successfully',
                'The goal has been removed from the program.'
            );
            setLocalGoals(prev => prev.filter(goal => goal.id !== id));
        }
    };

    const handleInputChange = (
        id: string,
        field: keyof GoalItem,
        value: string
    ) => {
        setLocalGoals(prev =>
            prev.map(goal =>
                goal.id === id ? { ...goal, [field]: value } : goal
            )
        );
    };

    const handleSave = async () => {
        const validationErrors: Record<string, string> = {};

        localGoals.forEach(goal => {
            if (!goal.type.trim()) {
                validationErrors[`type-${goal.id}`] = 'Goal Type is required';
            }
            if (!goal.description.trim()) {
                validationErrors[`description-${goal.id}`] =
                    'Description is required';
            }
            if (goal.targetValue && !/^\d+$/.test(goal.targetValue)) {
                validationErrors[goal.id] = 'Only numeric values are allowed';
            }
            if (goal.targetValue.trim() && !goal.unit.trim()) {
                validationErrors[`unit-${goal.id}`] = 'Unit is required';
            }
        });

        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            return;
        }

        try {
            const newGoals = localGoals.filter(g => g.isNew);
            const existingGoals = localGoals.filter(g => !g.isNew);

            const results = await Promise.all([
                ...existingGoals.map(goal =>
                    updateGoal(goal.id, {
                        goaltype: goal.type,
                        description: goal.description,
                        ...(goal.targetValue.trim() && {
                            targetvalue: Number(goal.targetValue),
                            unit: goal.unit,
                        }),
                        goaltypeid: goal.goalTypeId,
                        title: goal.type,
                    })
                ),
                ...newGoals.map(goal =>
                    createGoal({
                        userid: userId,
                        programid: programId,
                        title: goal.type,
                        goaltype: goal.type,
                        goaltypeid: goal.goalTypeId,
                        description: goal.description,
                        ...(goal.targetValue.trim() && {
                            targetvalue: Number(goal.targetValue),
                            unit: goal.unit,
                        }),
                        measurementtype: 'numeric',
                        istemplate:true,
                    })
                ),
            ]);

            const failure = results.find(result => !result.success);

            await fetchGoals();

            if (failure) {
                onError(failure.message || 'Failed to save program goals');
                return;
            }

            onSuccess(
                'Program Goals Updated',
                'Program goals have been updated successfully.'
            );
            onClose();
        } catch (err) {
            console.error(err);
            onError('Failed to update program goals');
        }
    };

const handleAddNewGoalTypeSubmit = async (
    e: React.FormEvent
) => {
    e.preventDefault();

    const trimmedName = newGoalTypeName.trim();

    if (!trimmedName) {
        setNewGoalTypeError('Goal Type Name cannot be empty');
        return;
    }

    try {
        const createdGoalType = await createGoalType({
            name: trimmedName,
        });

        if (!createdGoalType) {
            setNewGoalTypeError(
                'A goal type with this name already exists'
            );
            return;
        }

        // Close and clear the popup only.
        // Do not add a new goal row here.
        setNewGoalTypeName('');
        setNewGoalTypeError('');
        setShowNewGoalTypeModal(false);

        // Refresh the goal type dropdown options.
        // Existing unsaved goal changes will remain because
        // the useEffect depends only on `goals`.
        await fetchGoalTypes();

        onSuccess(
            'Goal Type Added',
            'New goal type created successfully.'
        );
    } catch (error: any) {
        console.error('Error adding goal type:', error);

        const message =
            error?.response?.data?.message ||
            error?.message ||
            'Failed to create goal type';

        setNewGoalTypeError(message);
    }
};

const openNewGoalTypeModal = () => {
    setNewGoalTypeName('');
    setNewGoalTypeError('');
    setShowNewGoalTypeModal(true);
};



    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h2 className={styles.title}>Update Program Details – Goals</h2>
                <div className={styles.actionButtons}>
    <button
    type="button"
    className={styles.cancelBtn}
    onClick={openNewGoalTypeModal}
>
    Add New Goal Type +
</button>
                    <button
                        type="button"
                        className={styles.cancelBtn}
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.saveBtn}
                        onClick={handleSave}
                    >
                        Save Changes
                    </button>
                </div>
            </header>

            <main className={styles.contentCard}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionTitle}>Program Goals:</span>
                    <hr className={styles.divider} />
                </div>

                <div className={styles.goalList}>
                    {localGoals.map(goal => (
                        <div key={goal.id} className={styles.goalRow}>
                            <div className={styles.statusContainer}>
                                <CheckIcon />
                            </div>

                            <div className={styles.formGrid}>
                                <div>
                                    <CustomSelect
                                        options={goalTypes.map(gt => ({
                                            label: gt.name,
                                            value: gt.name,
                                        }))}
                                        value={goal.type}
                                        placeholder="Select Goal Type"
                                        hasError={!!errors[`type-${goal.id}`]}
                                        onChange={selectedVal => {
                                            const selectedGoalType = goalTypes.find(
                                                gt => gt.name === selectedVal
                                            );
                                            setLocalGoals(prev =>
                                                prev.map(g =>
                                                    g.id === goal.id
                                                        ? {
                                                              ...g,
                                                              type: selectedGoalType?.name || '',
                                                              goalTypeId: selectedGoalType?.id || '',
                                                          }
                                                        : g
                                                )
                                            );
                                            setErrors(prev => ({
                                                ...prev,
                                                [`type-${goal.id}`]: '',
                                            }));
                                        }}
                                    />
                                    {errors[`type-${goal.id}`] && (
                                        <p className={styles.errorText}>
                                            {errors[`type-${goal.id}`]}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <input
                                        type="text"
                                        placeholder="Program Goal Description *"
                                        className={`${styles.input} ${
                                            errors[`description-${goal.id}`]
                                                ? styles.inputErrorBorder
                                                : ''
                                        }`}
                                        value={goal.description}
                                        onChange={e => {
                                            handleInputChange(
                                                goal.id,
                                                'description',
                                                e.target.value
                                            );
                                            if (e.target.value.trim()) {
                                                setErrors(prev => ({
                                                    ...prev,
                                                    [`description-${goal.id}`]: '',
                                                }));
                                            }
                                        }}
                                    />
                                    {errors[`description-${goal.id}`] && (
                                        <p className={styles.errorText}>
                                            {errors[`description-${goal.id}`]}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <input
                                        type="text"
                                        placeholder="Target Value"
                                        className={`${styles.input} ${
                                            errors[goal.id]
                                                ? styles.inputErrorBorder
                                                : ''
                                        }`}
                                        value={goal.targetValue}
                                        onPaste={e => {
                                            const pastedText = e.clipboardData.getData('text');
                                            if (!/^\d+$/.test(pastedText)) {
                                                e.preventDefault();
                                                setErrors(prev => ({
                                                    ...prev,
                                                    [goal.id]: 'Only numeric values are allowed',
                                                }));
                                            }
                                        }}
                                        onChange={e =>
                                            handleTargetValueChange(
                                                goal.id,
                                                e.target.value
                                            )
                                        }
                                    />
                                    {errors[goal.id] && (
                                        <p className={styles.errorText}>
                                            {errors[goal.id]}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <CustomSelect
                                        options={units.map(u => ({
                                            label: u,
                                            value: u,
                                        }))}
                                        value={goal.unit}
                                        placeholder="Unit"
                                        hasError={!!errors[`unit-${goal.id}`]}
                                        onChange={selectedVal => {
                                            handleInputChange(
                                                goal.id,
                                                'unit',
                                                selectedVal
                                            );
                                            setErrors(prev => ({
                                                ...prev,
                                                [`unit-${goal.id}`]: '',
                                            }));
                                        }}
                                    />
                                    {errors[`unit-${goal.id}`] && (
                                        <p className={styles.errorText}>
                                            {errors[`unit-${goal.id}`]}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className={styles.deleteContainer}>
                                <button
                                    type="button"
                                    className={styles.deleteBtn}
                                    onClick={() => handleDeleteGoal(goal.id)}
                                    aria-label="Delete Goal Row"
                                >
                                    <TrashIcon />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className={styles.addBtnContainer}>
                    <button
                        type="button"
                        className={styles.addGoalBtn}
                        onClick={handleAddGoal}
                    >
                        <span className={styles.plusIcon}></span> Add New Goal{' '}
                        <span> + </span>
                    </button>
                </div>
            </main>
                     

            {showNewGoalTypeModal && (
                <div className={styles.subModalOverlay}>
                    <div className={styles.subModalContainer}>

                        <div className={styles.modalHeader}>
                            <h3>New Goal Type</h3>

                            <button
                                type="button"
                                className={styles.closeButton}
                                onClick={() => {
                                    setShowNewGoalTypeModal(false);
                                    setNewGoalTypeName('');
                                    setNewGoalTypeError('');
                                }}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <line
                                        x1="18"
                                        y1="6"
                                        x2="6"
                                        y2="18"
                                    />
                                    <line
                                        x1="6"
                                        y1="6"
                                        x2="18"
                                        y2="18"
                                    />
                                </svg>
                            </button>
                        </div>

                        <form
                            onSubmit={handleAddNewGoalTypeSubmit}
                            className={styles.modalBody}
                        >
                            <div className={styles.formGroup}>
                                <label className={styles.fieldLabel}>
                                    Goal Type Name
                                </label>

                                <input
                                    type="text"
                                    className={`${styles.input} ${
                                        newGoalTypeError
                                            ? styles.inputErrorBorder
                                            : ''
                                    }`}
                                    placeholder="Add Goal Type Name"
                                    value={newGoalTypeName}
                                    onChange={e => {
                                        setNewGoalTypeName(
                                            e.target.value
                                        );

                                        if (e.target.value.trim()) {
                                            setNewGoalTypeError('');
                                        }
                                    }}
                                />

                                {newGoalTypeError && (
                                    <p className={styles.errorText}>
                                        {newGoalTypeError}
                                    </p>
                                )}
                            </div>

                            <div className={styles.formFooter}>
                                <button
                                    type="submit"
                                    className={styles.saveBtn}
                                    disabled={creating}
                                >
                                    {creating
                                        ? 'Adding...'
                                        : 'Add Goal Type'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
           
        </div>
    );
};
        